import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, BookOpen, Trophy, Briefcase, GraduationCap, Calendar, MapPin, Building2, FileText, Clock, Award, Mail, Info, Sun, Moon } from 'lucide-react';
import { SEO } from '../components/SEO';

const benefits = [
  'Átfogó szakmai alapok az ingatlanpiacról',
  'Jogi és gazdasági háttérismeretek',
  'Gyakorlati tárgyalástechnika és ügyfélkezelés',
  'Értékbecslés és piacismeret',
  'Marketing és önmenedzselés',
  'Mentorálás tapasztalt szakemberektől',
];

// Nappali képzés - Kecskemét
const nappalDetails = {
  school: 'Kecskeméti SZC Fazekas István Szakiskola',
  partner: 'Együttműködésben az Akadémia Plusz 2.0 Kft-vel',
  schedule: 'Hétköznapi napokon',
  license: 'B/2021/002289',
  program: 'Ingatlanközvetítő (Programkövetelmény száma: 04164005)',
  details: [
    'Az Akadémia reklámozhatja a képzést',
    'A jelentkezéseket a saját weboldalán keresztül gyűjti',
    'Az adatokat GDPR alapján kezeli és továbbítja a képző intézmény felé',
  ],
  officialData: [
    { label: 'Képző intézmény neve', value: 'Kecskeméti SZC Fazekas István Szakiskola' },
    { label: 'Képzés szakmai száma', value: '04164005' },
    { label: 'Képzés időtartama', value: '3 hónap' },
    { label: 'Képzés óraszáma', value: '247 óra' },
    { label: 'Szükséges előképzettség', value: 'Érettségi' },
    { label: 'Tanfolyam végét igazoló dokumentum', value: 'Tanúsítvány' },
  ],
  requirements: [
    'A bizonyítvány bemutatása a beiratkozás során kötelező.',
    'Amennyiben az eredeti bizonyítvány nincs meg, másolatot kell kérni az érintett iskolától.',
    'Idegennyelvű érettségit nem áll módunkban elfogadni, ebben az esetben hiteles fordítást kell készíttetni és azt bemutatni.',
  ],
  email: 'akademia@dh.hu',
};

// Esti képzés - Csepel
const estiDetails = {
  school: 'Budapesti Komplex SZC Szamos Mátyás Technikum és Szakképző Iskola',
  partner: 'Együttműködésben az Akadémia Plusz 2.0 Kft-vel',
  schedule: 'Hétvégi',
  location: 'Csepel – Budapest',
  program: 'Ingatlanközvetítő (Programkövetelmény száma: 04164005)',
  details: [
    'Az Akadémia reklámozhatja a képzést',
    'A jelentkezéseket a saját weboldalán keresztül gyűjti',
    'Az adatokat GDPR alapján kezeli és továbbítja a képző intézmény felé',
  ],
  officialData: [
    { label: 'Képző intézmény neve', value: 'Budapesti Komplex SZC Szamos Mátyás Technikum és Szakképző Iskola' },
    { label: 'Képzés szakmai száma', value: '04164005' },
    { label: 'Képzés időtartama', value: '3 hónap' },
    { label: 'Képzés óraszáma', value: '242 óra' },
    { label: 'Szükséges előképzettség', value: 'Érettségi' },
    { label: 'Tanfolyam végét igazoló dokumentum', value: 'Tanúsítvány' },
  ],
  requirements: [
    'Érettségi bizonyítvány bemutatása.',
    'Szükséges személyes adatok megadása a felnőttképzési szerződéshez.',
    'A képző intézmény által előírt adminisztratív feltételek teljesítése.',
  ],
  email: 'akademia@dh.hu',
};

export const RealEstatePage = () => {
  const [isNappali, setIsNappali] = useState(true);
  const currentDetails = isNappali ? nappalDetails : estiDetails;

  return (
    <main data-testid="realestate-page">
      <SEO
        title="Ingatlanközvetítői képzés"
        description="Államilag elismert ingatlanközvetítői szakképesítés megszerzése. Nappali és esti képzés, mentorálás, akár ingyenesen állami támogatással. Jelentkezz most!"
        path="/ingatlan"
      />
      {/* Hero */}
      <section className="relative min-h-[50vh] md:min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img loading="lazy"
            src="https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Modern budapesti irodaház — ingatlanközvetítői képzés helyszíne"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#404041]/70 via-[#404041]/50 to-[#404041]" />
        </div>

        <div className="container-custom relative z-10 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="font-heading font-bold text-white mb-6">
              <span className="block text-3xl md:text-5xl lg:text-6xl">
                Ingatlanközvetítői képzés Budapesten
              </span>
              <span className="block text-base md:text-xl lg:text-2xl font-medium text-brand-light-green mt-3">
                Építs karriert az ingatlanpiacon
              </span>
            </h1>
            
            {/* Official certification info */}
            <div className="glass-card p-6 mb-8">
              <div className="flex items-start gap-4">
                <GraduationCap size={28} className="text-brand-light-green flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-medium mb-2">
                    A 04164005 számú Ingatlanközvetítő, államilag elismert szakképesítés megszerzésére irányuló szakmai képzés
                  </p>
                  <p className="text-white/70 text-sm">
                    Akadémia Plusz 2.0 Kft. (E/2021/000035)
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xl text-white/70 mb-8">
              Átfogó szakmai képzés, mentorálás és folyamatos támogatás. 
              Nem csak tudást adunk, hanem valódi lehetőséget egy stabil karrierhez.
            </p>
            <Link
              to="/jelentkezes"
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="realestate-cta-booking"
            >
              Jelentkezem
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Training Type Switch & Details */}
      <section className={`section-padding transition-colors duration-500 ${isNappali ? 'bg-[#05435a]' : 'bg-[#1a1a2e]'}`}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.05 }}
            className="max-w-4xl mx-auto"
          >
            {/* Toggle Switch */}
            <div className="flex justify-center mb-12">
              <div className="glass-card p-2 inline-flex items-center gap-2">
                <button
                  onClick={() => setIsNappali(true)}
                  className={`flex items-center gap-2 px-4 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-medium transition-all duration-300 ${
                    isNappali
                      ? 'bg-brand-orange text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                  data-testid="toggle-nappali"
                >
                  <Sun size={18} />
                  Nappali képzés
                </button>
                <button
                  onClick={() => setIsNappali(false)}
                  className={`flex items-center gap-2 px-4 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-medium transition-all duration-300 ${
                    !isNappali
                      ? 'bg-[#56999B] text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                  data-testid="toggle-esti"
                >
                  <Moon size={18} />
                  Esti képzés
                </button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={isNappali ? 'nappali' : 'esti'}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Header */}
                <div className="text-center mb-8">
                  <h2 className="font-heading text-4xl font-semibold text-white mb-2">
                    {isNappali ? 'Nappali képzés' : 'Esti képzés'}
                  </h2>
                  <p className={`text-lg ${isNappali ? 'text-brand-light-green' : 'text-[#56999B]'}`}>
                    {currentDetails.school}
                  </p>
                  <p className="text-white/60 text-sm mt-1">{currentDetails.schedule}</p>
                  <div className="mt-4 inline-flex items-center gap-2 bg-[#FBAF3F]/15 border border-[#FBAF3F]/30 px-5 py-2.5">
                    <MapPin size={18} className="text-[#FBAF3F] flex-shrink-0" />
                    <p className="text-white text-sm font-medium">
                      {isNappali
                        ? 'Helyszín: 1027 Budapest, Kapás utca 6–12.'
                        : 'Helyszín: BKSZC Szamos Mátyás Technikum és Szakképző Iskola, Csepel'}
                    </p>
                  </div>
                </div>

                {/* Content based on selected type */}
                {isNappali ? (
                  <>
                    {/* Nappali - Full details */}
                    <h3 className="font-heading text-2xl font-semibold text-white mb-6 text-center">
                      A képzés adatai
                    </h3>

                    {/* Main Details Grid */}
                    <div className="glass-card p-5 md:p-8 mb-8">
                      <div className="grid md:grid-cols-2 gap-6">
                        {nappalDetails.officialData.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <CheckCircle size={18} className="text-brand-light-green flex-shrink-0 mt-1" />
                            <div>
                              <p className="text-white/60 text-sm">{item.label}</p>
                              <p className="text-white font-medium">{item.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Additional Info Cards */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="glass-card p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Info size={20} className="text-brand-orange" />
                          <h4 className="text-white font-semibold">Bekapcsolódási feltétel</h4>
                        </div>
                        <p className="text-white/70 text-sm leading-relaxed">
                          Bekapcsolódhat, aki a tudásmérésen képzési részenként legalább 60%-os teljesítményt ér el.
                        </p>
                      </div>

                      <div className="glass-card p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Clock size={20} className="text-brand-orange" />
                          <h4 className="text-white font-semibold">Rövidített képzés</h4>
                        </div>
                        <p className="text-white/70 text-sm leading-relaxed">
                          A képzést megfelelő előzetes ismeretekkel rendelkezőknek 151 órában valósítja meg a képző. 
                          (A jelentkezésnek nem feltétele ingatlanközvetítői múlt, képző intézmény kizárólag a meglévő ismereteket vizsgálja.)
                        </p>
                      </div>
                    </div>

                    {/* Requirements */}
                    <div className="glass-card p-6 mb-8">
                      <div className="flex items-center gap-3 mb-4">
                        <Award size={20} className="text-brand-light-green" />
                        <h4 className="text-white font-semibold">Beiratkozási követelmények</h4>
                      </div>
                      <ul className="space-y-3 text-white/70 text-sm">
                        {nappalDetails.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-brand-dark-green flex-shrink-0 mt-1" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Cooperation details */}
                    <div className="glass-card p-6 mb-8">
                      <div className="flex items-center gap-3 mb-4">
                        <Building2 size={20} className="text-brand-light-green" />
                        <h4 className="text-white font-semibold">Együttműködés részletei</h4>
                      </div>
                      <p className="text-white/70 text-sm mb-4">{nappalDetails.partner}</p>
                      <ul className="space-y-2 text-white/60 text-sm">
                        {nappalDetails.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-brand-dark-green flex-shrink-0 mt-0.5" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                      <p className="text-white/50 text-sm mt-4">
                        Felnőttképzési engedély száma: <span className="text-white/80">{nappalDetails.license}</span>
                      </p>
                    </div>

                    {/* Pricing Info */}
                    <div className="glass-card p-6 border-brand-orange/30">
                      <div className="flex items-center gap-3 mb-4">
                        <Mail size={20} className="text-brand-orange" />
                        <h4 className="text-white font-semibold">Képzési díj</h4>
                      </div>
                      <p className="text-white/70 mb-4">
                        A képzés a képző által akár <span className="text-brand-light-green font-semibold">ingyenesen, állami támogatással</span> is elvégezhető.
                      </p>
                      <p className="text-white/60 text-sm">
                        A részletekről érdeklődjön: <a href={`mailto:${nappalDetails.email}`} className="text-brand-light-green hover:underline">{nappalDetails.email}</a>
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Esti - Hamarosan elérhető */}
                    <div className="glass-card p-8 md:p-12 text-center">
                      <div className="w-16 h-16 bg-[#56999B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Clock size={32} className="text-[#56999B]" />
                      </div>
                      <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
                        Hamarosan elérhető
                      </h3>
                      <p className="text-white/60 text-lg mb-3 max-w-xl mx-auto">
                        Az esti képzés részleteit jelenleg egyeztetjük a képző intézménnyel.
                        Az indulás időpontját és a pontos feltételeket hamarosan közzétesszük.
                      </p>
                      <p className="text-white/50 text-sm mb-8">
                        Helyszín: BKSZC Szamos Mátyás Technikum és Szakképző Iskola, Csepel
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                          onClick={() => setIsNappali(true)}
                          className="btn-primary inline-flex items-center justify-center gap-2 group"
                        >
                          Nappali képzés megtekintése
                          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <Link
                          to="/#kapcsolat"
                          className="btn-outline inline-flex items-center justify-center gap-2"
                        >
                          Értesítést kérek
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="section-padding bg-[#404041]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                Kinek szól ez a képzés?
              </h2>
              <div className="space-y-4 text-white/70 text-lg">
                <p>
                  Mindenkinek, aki stabil, jól fizető karriert szeretne építeni egy dinamikus piacon. 
                  Nincs szükség előzetes tapasztalatra — a motiváció és a tanulási hajlandóság a lényeg.
                </p>
                <p>
                  Ideális pályakezdőknek, karrierváltóknak, vagy azoknak, akik már dolgoznak az ingatlanpiacon, 
                  de profi alapokat szeretnének. A képzés végén nem csak papírt kapsz, hanem valódi készségeket és kapcsolatokat.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: Users, label: 'Pályakezdők' },
                { icon: Briefcase, label: 'Karrierváltók' },
                { icon: BookOpen, label: 'Tanulni vágyók' },
                { icon: Trophy, label: 'Ambiciózus szakemberek' },
              ].map((item, idx) => (
                <div key={idx} className="glass-card p-6 text-center">
                  <item.icon size={32} className="text-brand-light-green mx-auto mb-3" />
                  <p className="text-white font-medium">{item.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="section-padding bg-[#05435a]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            className="max-w-3xl mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Mit kapsz a képzés során?
            </h2>
            <p className="text-white/60 text-lg">
              Nem csak elméletet, hanem azonnal alkalmazható tudást és valódi üzleti lehetőségeket.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ delay: idx * 0.05 }}
                className="flex items-center gap-4 p-4 glass-card"
              >
                <CheckCircle size={24} className="text-brand-light-green flex-shrink-0" />
                <p className="text-white">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#404041]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            className="glass-card brand-glow p-6 md:p-12 lg:p-16 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">
              Készen állsz a változásra?
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
              Válaszd ki a számodra megfelelő képzési formát és jelentkezz még ma. 
              Kérdésed van? Keress minket bátran!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/jelentkezes"
                className="btn-primary inline-flex items-center justify-center gap-2 group"
                data-testid="realestate-bottom-cta"
              >
                Jelentkezem
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/#kapcsolat"
                className="btn-outline inline-flex items-center justify-center gap-2"
              >
                Kérdésem van
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default RealEstatePage;
